import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import DocumentationPage from './pages/DocumentationPage';
import UploadPage from './pages/UploadPage';
import SearchPage from './pages/SearchPage';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import UserGuidePage from './pages/UserGuidePage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AdkAgentPage from './pages/AdkAgentPage';
import SowBuilderPage from './pages/SowBuilderPage';
import SowQuestionnairePage from './pages/SowQuestionnairePage';
import SowDraftPage from './pages/SowDraftPage';
import DesignBuilderPage from './pages/DesignBuilderPage';
import DesignDraftPage from './pages/DesignDraftPage';
import CodeDocPage from './pages/CodeDocPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowPasswordChange = false }) => {
  const { isAuthenticated, loading, mustChangePassword } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-[#2B8FD8] border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If user must change password and this is not the change-password page, redirect
  if (mustChangePassword && !allowPasswordChange && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" state={{ forced: true }} replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, mustChangePassword } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-[#2B8FD8] border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    // If user must change password, redirect to change-password page
    if (mustChangePassword) {
      return <Navigate to="/change-password" state={{ forced: true }} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      
      {/* Change Password Route (Protected - user must be authenticated, allows password change) */}
      <Route path="/change-password" element={<ProtectedRoute allowPasswordChange={true}><ChangePasswordPage /></ProtectedRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
      <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetailPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
      <Route path="/documentation" element={<ProtectedRoute><DocumentationPage /></ProtectedRoute>} />
      <Route path="/documentation/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
      <Route path="/sow-builder" element={<ProtectedRoute><SowBuilderPage /></ProtectedRoute>} />
      <Route path="/sow-builder/questions/:id" element={<ProtectedRoute><SowQuestionnairePage /></ProtectedRoute>} />
      <Route path="/sow-builder/:id" element={<ProtectedRoute><SowDraftPage /></ProtectedRoute>} />
      <Route path="/design-builder" element={<ProtectedRoute><DesignBuilderPage /></ProtectedRoute>} />
      <Route path="/design-builder/:id" element={<ProtectedRoute><DesignDraftPage /></ProtectedRoute>} />
      <Route path="/code-docs" element={<ProtectedRoute><CodeDocPage /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/ai-agent" element={<ProtectedRoute><AdkAgentPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/userguide" element={<ProtectedRoute><UserGuidePage /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
